import CommerceApiClient from '@reservamos/commerce-api-client';
import errorMonitoring from '@/errorMonitoring';
import store from '@/store';
import isAccountWebTransport from '@/utils/accountWebTransport';

interface StoreState {
  whitelabelConfig?: {
    env?: {
      brand?: string;
      reserhub?: {
        api?: {
          baseUrl?: string;
        };
      };
    };
  };
}

let baseUrl: string | null = null;
let client: CommerceApiClient | null = null;
let authToken: string | null = null;

function getConfiguredBaseUrl(): string | null {
  const state = store.getState() as StoreState;
  return state.whitelabelConfig?.env?.reserhub?.api?.baseUrl || null;
}

/** Initialize the generated client with the active whitelabel configuration. */
function initializeCommerceApiClient(): void {
  if (isAccountWebTransport((store.getState() as StoreState).whitelabelConfig?.env?.brand)) {
    authToken = null;
  }
  baseUrl = getConfiguredBaseUrl();
  client = baseUrl
    ? new CommerceApiClient(baseUrl, {
        requestInit: {
          credentials: 'include',
          ...(authToken ? { headers: { Authorization: `Bearer ${authToken}` } } : {}),
        },
      })
    : null;
}

/**
 * Set the session token used to authenticate account requests.
 *
 * Only the native app host supplies one; web authenticates with the `reserhub-auth-session`
 * cookie. The value must be a Stytch session token — the gateway passes it to
 * `getUserBySessionToken`, the same call it makes for the cookie.
 *
 * Marked CDS Account WebViews use cookies and discard native bearer tokens. Other clients
 * send bearer tokens alongside cookies because the funnel cannot tell which
 * credential is live — the cookie is `httpOnly`.
 *
 * Caveat while debugging a 401 from `/accounts/auth/me` inside the app: the gateway validates only
 * the first credential it finds (`cookieSessionToken || headerAuthorizationToken` in
 * `commerce/accounts/gateways/auth.gateway.ts`), so a lapsed cookie shadows a good token and the
 * token is never tested — check the request for a `Cookie: reserhub-auth-session` header first.
 * Two independent fixes each close this, and neither has shipped: the app clearing that cookie from
 * its WebView jar (MOB-444 part 3, tasks.md 8.12) and a gateway credential fallback (tasks.md 9.2).
 *
 * The generated client bakes `requestInit` in at construction, so changing the token rebuilds it.
 *
 * Held in memory only: a token is a credential and must not outlive the page. The native host
 * republishes it on every load (see `readNativeAuthToken`).
 *
 * @param token - Session token, or a falsy value to clear it.
 */
function setAuthToken(token: string | null | undefined): void {
  const next = isAccountWebTransport((store.getState() as StoreState).whitelabelConfig?.env?.brand)
    ? null
    : token || null;
  if (next === authToken) return;

  authToken = next;
  // `requestInit` is captured when the client is constructed, so the header only changes on rebuild.
  initializeCommerceApiClient();
}

/** Whether a bearer token is currently set. */
function hasAuthToken(): boolean {
  return Boolean(authToken);
}

/** Return the generated client configured for the active whitelabel. */
function getCommerceApiClient(): CommerceApiClient {
  const configuredBaseUrl = getConfiguredBaseUrl();
  if (!client || configuredBaseUrl !== baseUrl) initializeCommerceApiClient();

  if (!client) {
    const error = new Error('Reserhub API is not initialized');
    errorMonitoring.notify({
      error,
      info: { from: 'getCommerceApiClient' },
    });
    throw error;
  }

  return client;
}

export { getCommerceApiClient, initializeCommerceApiClient, setAuthToken, hasAuthToken };
